/** SegmentedTabs: equal-width choices (each at least as wide as its label) with a dark pill that slides to the chosen one. An option's `hint` is its tooltip and description. */
import { useLayoutEffect, useRef, useState } from "react";
import { ToggleGroup } from "radix-ui";
import { cx, focusRing } from "./cx";
import { Tooltip } from "./Tooltip";

const GAP = 8;

interface SegmentedTabsProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; hint?: string }>;
  label: string;
  className?: string;
}

export function SegmentedTabs<T extends string>({ value, onChange, options, label, className }: SegmentedTabsProps<T>) {
  const count = options.length;
  const index = options.findIndex((option) => option.value === value);
  const width = `(100% - ${GAP * (count - 1)}px) / ${count}`;
  const root = useRef<HTMLDivElement>(null);
  // The hint shows on hover (the label's own tooltip) and on keyboard focus of the item (which the label cannot have).
  const [hovered, setHovered] = useState<T | null>(null);
  const [focused, setFocused] = useState<T | null>(null);
  // A focus that a press brings is the pointer's, not the keyboard's: as Radix's own trigger, it shows no hint.
  const pressing = useRef(false);
  // The pill takes the chosen item's own box, so a label that needs more room (larger text, wider spacing) still sits inside it.
  const [box, setBox] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const group = root.current;
    if (!group) return;
    const items = group.querySelectorAll<HTMLElement>("[data-slot=toggle-group-item]");
    const measure = () => {
      const item = items[index];
      setBox(item && item.offsetWidth > 0 ? { left: item.offsetLeft, width: item.offsetWidth } : null);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    // The group and every item: a label that changes width (the font landing, wider text spacing) moves its neighbours too.
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [index, count]);
  return (
    <ToggleGroup.Root
      ref={root}
      type="single"
      data-slot="toggle-group"
      value={value}
      onValueChange={(next) => next && onChange(next as T)}
      aria-label={label}
      className={cx("relative flex items-stretch gap-2", className)}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 min-h-8 rounded-lg border border-primary bg-[linear-gradient(180deg,var(--color-primary-from)_0%,var(--color-primary)_64.697%)] transition-[left,width] duration-200 ease-out-smooth"
        style={box ? { width: box.width, left: box.left } : { width: `calc(${width})`, left: `calc((${width} + ${GAP}px) * ${index})` }}
      />
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.value}
          value={option.value}
          data-slot="toggle-group-item"
          aria-description={option.hint}
          onPointerDown={() => (pressing.current = true)}
          onFocus={() => {
            if (option.hint && !pressing.current) setFocused(option.value);
            pressing.current = false;
          }}
          onBlur={() => setFocused((now) => (now === option.value ? null : now))}
          onKeyDown={(event) => event.key === "Escape" && setFocused(null)}
          className={cx(
            "relative z-10 flex min-h-8 min-w-max flex-1 basis-0 items-center justify-center whitespace-nowrap rounded-lg border-hair border-border bg-card px-2.5 text-body font-medium leading-none text-secondary-foreground transition-[color,background-color,border-color,transform] duration-200 hover:bg-control-hover active:scale-[0.97] data-[state=on]:border-transparent data-[state=on]:bg-transparent data-[state=on]:text-primary-foreground",
            focusRing,
          )}
        >
          {/* The hint's tooltip hangs on the label, not the item: a Radix Tooltip trigger sets its own data-state, which would overwrite the item's "on". It is opened by the label's hover and the item's keyboard focus. */}
          {option.hint ? (
            <Tooltip content={option.hint} open={hovered === option.value || focused === option.value} onOpenChange={(open) => setHovered((now) => (open ? option.value : now === option.value ? null : now))}>
              <span className="flex h-full items-center">{option.label}</span>
            </Tooltip>
          ) : (
            option.label
          )}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
